
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { EmailSettings } from "@/hooks/admin/settings/useEmailSettings";
import { useIsMobile } from "@/hooks/use-mobile";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Info } from "lucide-react";

export interface SmtpSettingsProps {
  settings: EmailSettings;
  onInputChange: (key: keyof EmailSettings, value: string | boolean) => void;
  saveSettings?: () => Promise<{ success: boolean; error?: string }>;
}

export const SmtpSettings: React.FC<SmtpSettingsProps> = ({
  settings,
  onInputChange,
  saveSettings,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const isMobile = useIsMobile();

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);

      if (saveSettings) {
        const result = await saveSettings();
        if (!result.success) {
          throw new Error(result.error || "Failed to save settings");
        }
      }

      toast.success("Email settings saved successfully");
    } catch (error: any) {
      console.error("Error saving email settings:", error);
      toast.error(`Failed to save settings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-medium">Email Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Configure the email settings for sending notifications
        </p>
      </div>

      <Alert variant="info" className="border-blue-200 dark:border-blue-800">
        <Info className="h-4 w-4 text-blue-500 dark:text-blue-400" />
        <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
          <strong>Domain Verification Required:</strong> The "From Email
          Address" domain must be verified in your Resend account. Use an email
          address that belongs to a domain you've verified (e.g.,{" "}
          <code>NationCard™ &lt;sender@notifications.nationcard.org&gt;</code> for the verified
          subdomain <code>notifications.nationcard.org</code>).
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div
          className={`${
            isMobile
              ? "flex flex-col space-y-2"
              : "flex items-center justify-between"
          }`}
        >
          <Label className="font-medium">Enable Email Notifications</Label>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => onInputChange("enabled", checked)}
            className={isMobile ? "self-start" : ""}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="from-email">From Email Address</Label>
          <Input
            id="from-email"
            value={settings.fromAddress}
            onChange={(e) => onInputChange("fromAddress", e.target.value)}
            disabled={!settings.enabled}
            className="w-full"
            placeholder="NationCard™ <sender@notifications.nationcard.org>"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Must use a verified domain. Check your domain verification status in
            your Resend account.
          </p>
        </div>
      </div>

      <Button
        onClick={handleSaveSettings}
        disabled={isSaving || !settings.enabled}
        className={isMobile ? "w-full" : "ml-auto block"}
      >
        {isSaving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
};
